import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import axiosInstance from "../../../utils/axiosInstance";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

function formatMoney(v) {
  if (v === null || v === undefined) return "-";
  return `₹${Number(v).toLocaleString("en-IN")}`;
}

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function PaymentBadge({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "paid")
    return <span className="text-xs text-green-600 font-medium">Paid</span>;
  if (s === "pending")
    return <span className="text-xs text-yellow-600 font-medium">Pending</span>;
  if (s === "failed")
    return <span className="text-xs text-red-600 font-medium">Failed</span>;
  return <span className="text-xs text-gray-500">{status || "-"}</span>;
}

function ReportBadge({ ready }) {
  if (ready === true) {
    return (
      <span className="text-xs text-blue-600 font-medium">Dispatched</span>
    );
  }
  if (ready === false) {
    return (
      <span className="text-xs text-gray-500 font-medium">Processing</span>
    );
  }
  return <span className="text-xs text-gray-500">-</span>;
}

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${API_BASE}/api/orders`, {
        params: { limit: 5 },
        withCredentials: true,
      });
      setOrders(res.data?.orders || []);
    } catch (err) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Recent Orders
            </h2>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/orders-management")}
            className="text-xs py-1 px-3"
          >
            View All
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">
                Order
              </th>
              <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">
                Patient
              </th>
              <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">
                Doctor
              </th>
              <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">
                Center
              </th>
              <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">
                Amount
              </th>
              <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">
                Date | Payment | Report
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan="6" className="py-4 text-center">
                  <p className="text-xs text-gray-500">Loading orders...</p>
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-4 text-center">
                  <p className="text-xs text-gray-500">No orders found</p>
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr
                  key={order.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {/* Order ID */}
                  <td className="py-2 px-4">
                    <div className="font-medium text-gray-900">
                      { `ORD-${order.id}`}
                    </div>
                    <div className="text-gray-400 capitalize">{order.status || ""}</div>
                  </td>

                  {/* Patient Details */}
                  <td className="py-2 px-4">
                    <div>
                      <div className="font-medium text-gray-900">
                        {order.patient?.fullName || "-"}
                      </div>
                      <div className="text-gray-500">
                        {order.patient?.contactNo || "-"}
                      </div>
                    </div>
                  </td>

                  {/* Doctor */}
                  <td className="py-2 px-4">
                    {order.doctor?.name ? (
                      <div className="font-medium text-gray-900">
                        {order.doctor.name}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>

                  {/* Center */}
                  <td className="py-2 px-4">
                    <div>
                      <span className="font-medium text-gray-900">
                        {order.isHomeSample ? "Home" : "Center"}
                      </span>
                      <div className="text-gray-500">
                        {order.isHomeSample
                          ? order.address?.city || "-"
                          : order.center?.name || "-"}
                      </div>
                    </div>
                  </td>

                  {/* Amount */}
                  <td className="py-2 px-4">
                    <div className="font-semibold text-gray-900">
                      {formatMoney(order.finalAmount)}
                    </div>
                    {Number(order.discount) > 0 && (
                      <div className="text-green-600">-{formatMoney(order.discount)}</div>
                    )}
                  </td>

                  {/* Date | Payment | Report */}
                  <td className="py-2 px-4">
                    <div>
                      <div className="text-gray-700">
                        {formatDate(order.createdAt)}
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <PaymentBadge status={order.paymentStatus} />
                        <ReportBadge ready={order.reportReady} />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}